import { setMarkerProperty } from "@/assets/utilities/tinymce/helpers/markerStyle";
import { createNumberInputGuards } from "@/assets/utilities/tinymce/helpers/numberInput";
import { CUSTOM_MARKERS_CLASS, MARKER_SIZE_ICON, MARKER_SIZE_UNITS } from "@/assets/utilities/tinymce/constants";

const NO_LIST_MESSAGE = "Place the cursor in a list to set its bullet/number size.";
const SIZE_PATTERN = /^(\d*\.?\d+)(px|em)$/;

const { sanitise, toCssNumber } = createNumberInputGuards({ allowNegative: false });

const getSelectedList = editor => editor.dom.getParent(editor.selection.getNode(), "ul,ol");

const directListItems = list => Array.prototype.filter.call(list.children, child => child.nodeName === "LI");

const syncMarkerLines = (editor, list) => {
  const body = editor.getBody();

  if (!list && !body) {
    return false;
  }

  let changed = false;
  const items = list ? directListItems(list) : editor.dom.select("li", body);

  items.forEach(li => {
    const parentList = editor.dom.getParent(li, "ul,ol");
    const drawsMarkers = parentList && editor.dom.hasClass(parentList, CUSTOM_MARKERS_CLASS);
    const sized = drawsMarkers && li.querySelector('[style*="font-size"]');
    const belongsToThisItem = sized && editor.dom.getParent(sized, "li") === li;
    const desired = belongsToThisItem ? sized.style.fontSize : "";

    if (desired === li.style.getPropertyValue("--marker-line")) {
      return;
    }

    setMarkerProperty(li, "--marker-line", desired);
    changed = true;
  });

  return changed;
};

const readCurrent = list => {
  const parsed = SIZE_PATTERN.exec(list.style.getPropertyValue("--marker-size").trim());

  if (!parsed) {
    return { size: "", unit: "em" };
  }

  return { size: parsed[1], unit: parsed[2] };
};

const applySize = (editor, list, value) => {
  editor.undoManager.transact(() => {
    if (value) {
      editor.dom.addClass(list, CUSTOM_MARKERS_CLASS);
    } else {
      editor.dom.removeClass(list, CUSTOM_MARKERS_CLASS);
    }

    setMarkerProperty(list, "--marker-size", value);
    syncMarkerLines(editor, list);
  });

  editor.fire("change");
};

const openMarkerSizeDialog = (editor, list) => {
  editor.windowManager.open({
    title: "Bullet/number size",
    body: {
      type: "panel",
      items: [
        {
          type: "input",
          name: "size",
          label: "Size",
        },
        {
          type: "listbox",
          name: "unit",
          label: "Unit",
          items: MARKER_SIZE_UNITS,
        },
      ],
    },
    initialData: readCurrent(list),
    buttons: [
      {
        type: "custom",
        name: "removeButton",
        text: "Remove",
        align: "start",
      },
      {
        type: "cancel",
        name: "closeButton",
        text: "Cancel",
      },
      {
        type: "submit",
        name: "submitButton",
        text: "Apply",
        primary: true,
      },
    ],
    onChange: function (api) {
      const { size } = api.getData();
      const sanitised = sanitise(size);

      if (sanitised !== size) {
        api.setData({ size: sanitised });
      }
    },
    onAction: function (api, details) {
      if (details.name === "removeButton") {
        applySize(editor, list, "");
        api.close();
      }
    },
    onSubmit: function (api) {
      const { size, unit } = api.getData();
      const value = toCssNumber(size);

      applySize(editor, list, value === "" ? "" : `${value}${unit}`);
      api.close();
    },
  });
};

const registerMarkerSize = editor => {
  editor.ui.registry.addIcon("markerSize", MARKER_SIZE_ICON);

  editor.ui.registry.addButton("markerSize", {
    icon: "markerSize",
    tooltip: "Bullet/number size",
    onAction: () => {
      const list = getSelectedList(editor);

      if (!list) {
        editor.notificationManager.open({ text: NO_LIST_MESSAGE, type: "info", timeout: 3000 });
        return;
      }

      openMarkerSizeDialog(editor, list);
    },
    onSetup: api => {
      const update = () => api.setDisabled(!getSelectedList(editor));

      update();
      editor.on("NodeChange", update);

      return () => editor.off("NodeChange", update);
    },
  });

  editor.on("NodeChange ExecCommand SetContent", () => {
    if (syncMarkerLines(editor)) {
      editor.fire("change");
    }
  });
};

export { registerMarkerSize };
